import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { subscribe } from "./events";
import {
  AUTHEVENT_LOGOUT,
  setSessionConfig,
  authFetch,
  getConfig,
  generatePkce,
  beginLogout,
  endLogout,
  isLoggingOut,
  tryRefreshToken,
  refreshSession,
  validateRedirect,
  logoutBroadcast,
  subscribeLogoutBroadcast,
} from "./session";

const AuthContext = createContext(null);
const UserContext = createContext({
  userId: undefined,
  permissions: [],
  hasPermission: () => false,
  hasAccess: false,
  canReview: false,
});

/**
 * Should the provider skip its bootstrap (loadMe / silent SSO) on this path?
 *
 * Always skips /auth/callback (AuthCallback runs its own token exchange).
 * `transitPaths` lets a consumer add routes it merely HOSTS for other apps'
 * login flows — e.g. the auth service serves /p/login and /p/filedrop/:code.
 * Bootstrapping there would issue this app's own cookie and burn an SSO
 * exchange the visiting flow doesn't benefit from. Entries ending in "/" are
 * treated as path prefixes; others must match exactly.
 */
function _skipBootstrap(pathname, transitPaths) {
  if (pathname === "/auth/callback") return true;
  return transitPaths.some((p) =>
    p.endsWith("/") ? pathname.startsWith(p) : pathname === p
  );
}

/**
 * AuthProvider — cookie-based session.
 *
 * Required props:
 *   authUrl           e.g. "https://auth.talis-ai.com" (must be HTTPS or localhost)
 *   appId             e.g. "orbit" — used as JWT audience server-side
 *   allowedRedirects  array of allowed origin+path prefixes for post-login redirects
 *
 * Optional:
 *   appUrl            this app's base URL (used as default post-logout redirect)
 *   ssoStartPath      e.g. "/sso/start". For apps on registrable domains other
 *                     than talis-ai.com (cookies can't be shared across domains).
 *                     When set, the provider skips silent SSO (which requires the
 *                     .talis-ai.com SSO cookie) and instead redirects to this
 *                     same-origin path on the app's own backend, which runs the
 *                     OIDC redirect dance via the ait_auth_oidc Python SDK.
 *   transitPaths      routes this app merely HOSTS for other apps' login flows
 *                     (e.g. the auth service's /p/login, /p/filedrop/). The
 *                     provider skips its bootstrap there so it doesn't issue its
 *                     own cookie / burn an SSO exchange. Trailing "/" = prefix.
 *   redirectOnLogout  whether logout navigates to appUrl after clearing local
 *                     state (default true). Set false for SPA apps that prefer
 *                     to re-render their own login route without a full reload.
 *
 * The provider stores ONLY the user profile (from /api/me), never the access
 * or refresh tokens — those are httpOnly cookies the SPA cannot read by design.
 */
export function AuthProvider({ authUrl, appId, appUrl, allowedRedirects, ssoStartPath, transitPaths = [], redirectOnLogout = true, children }) {
  // setSessionConfig hard-fails on missing/invalid args; let it surface.
  const [configError, setConfigError] = useState(null);
  useEffect(() => {
    try {
      setSessionConfig({ authUrl, appId, allowedRedirects });
    } catch (e) {
      setConfigError(e.message);
    }
  }, [authUrl, appId, allowedRedirects]);

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async (skipRetry = false) => {
    try {
      const opts = skipRetry ? { _skipRetry: true } : {};
      const { appId } = getConfig();
      const res = await authFetch(`/api/me?app=${encodeURIComponent(appId)}`, opts);
      if (!res.ok) {
        setUser(null);
        return false;
      }
      const data = await res.json();
      setUser(data);
      return true;
    } catch {
      setUser(null);
      return false;
    }
  }, []);

  // Silent SSO exchange — if the user is logged in on another app (sso_session
  // exists) but this app has no cookie yet, issue one without a redirect.
  const trySilentSSO = useCallback(async () => {
    try {
      const statusRes = await authFetch("/api/sso/status", { _skipRetry: true });
      if (!statusRes.ok) return false;
      const { authenticated } = await statusRes.json();
      if (!authenticated) return false;

      const { verifier, challenge } = await generatePkce();
      const { appId } = getConfig();
      const callbackUrl = `${appUrl || authUrl}/auth/callback`;
      const params = new URLSearchParams({
        redirect_uri: callbackUrl,
        app: appId,
        code_challenge: challenge,
        code_challenge_method: "S256",
      });
      const checkRes = await authFetch(`/api/sso/check?${params}`, { _skipRetry: true });
      if (!checkRes.ok) return false;
      const checkData = await checkRes.json();
      if (checkData.status !== "authenticated" || !checkData.code) return false;

      const exchangeRes = await authFetch("/api/token/exchange", {
        method: "POST",
        body: JSON.stringify({
          code: checkData.code,
          app_id: appId,
          code_verifier: verifier,
        }),
      });
      if (!exchangeRes.ok) return false;

      return await fetchMe(false);
    } catch {
      return false;
    }
  }, [fetchMe, appUrl, authUrl]);

  // Bootstrap: try /api/me → if 401:
  //   - cross-domain apps (ssoStartPath set): redirect to backend /sso/start
  //   - same-domain (.talis-ai.com) apps: try silent SSO → if that fails, show login
  const loadMe = useCallback(async () => {
    const ok = await fetchMe(true);
    if (!ok) {
      if (ssoStartPath) {
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = `${ssoStartPath}?next=${next}`;
        return;
      }
      await trySilentSSO();
    }
    setLoading(false);
  }, [fetchMe, trySilentSSO, ssoStartPath]);

  // Post-login — cookies just set, allow retry.
  const login = useCallback(async () => {
    await fetchMe(false);
    setLoading(false);
  }, [fetchMe]);

  const logout = useCallback(async () => {
    if (isLoggingOut()) return;
    // Only POST /api/logout if we actually had a session. Calling it with
    // no cookies burns a rate-limit slot and contributes nothing, and in a
    // misbehaving bootstrap can feed a retry loop.
    const hadSession = user !== null;
    beginLogout();
    // Cross-domain apps route logout through their own backend's /sso/logout,
    // which redirects to the IdP's /api/oauth/logout end-session endpoint.
    // POSTing /api/logout cross-origin would fail (no .talis-ai.com cookies).
    if (ssoStartPath) {
      logoutBroadcast();
      setUser(null);
      setTimeout(() => endLogout(), 0);
      window.location.href = "/sso/logout";
      return;
    }
    if (hadSession) {
      try {
        await authFetch("/api/logout", { method: "POST" });
      } catch {
        // ignore network errors
      }
      logoutBroadcast();
    }
    setUser(null);
    setTimeout(() => endLogout(), 0);
    // If the consumer provided an appUrl and it's in the allowlist, navigate
    // there. Otherwise stay put — the consumer app is responsible for
    // rendering its login page when useAuth().user is null. SPA consumers can
    // opt out entirely via redirectOnLogout={false}.
    if (redirectOnLogout && appUrl && validateRedirect(appUrl) && window.location.href !== appUrl) {
      window.location.href = appUrl;
    }
  }, [user, appUrl, ssoStartPath, redirectOnLogout]);

  // Soft logout — local state only; server-side session is presumed dead.
  const softLogout = useCallback(() => {
    if (isLoggingOut()) return;
    beginLogout();
    setUser(null);
    logoutBroadcast();
    setTimeout(() => endLogout(), 0);
  }, []);

  // Initial bootstrap — skip on /auth/callback (AuthCallback handles its own
  // exchange + login()) and on any consumer-declared transitPaths, to avoid a
  // race where loadMe 401s before the token exchange has completed and to keep
  // hosted-login transit routes from issuing this app's own cookie.
  //
  // Depend on a stable string key, not the array itself: an inline literal (or
  // the default []) is a fresh reference every render and would otherwise
  // re-run this effect — and re-trigger loadMe — on every render.
  const transitKey = transitPaths.join(" ");
  useEffect(() => {
    if (configError || _skipBootstrap(window.location.pathname, transitPaths)) {
      setLoading(false);
      return;
    }
    loadMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configError, loadMe, transitKey]);

  // In-app logout event (from a 401 interceptor, etc.)
  useEffect(() => {
    const unsubscribe = subscribe(AUTHEVENT_LOGOUT, () => logout());
    return () => unsubscribe();
  }, [logout]);

  // Cross-tab logout via BroadcastChannel — clear local state, no navigate
  // (consumer renders Login when user is null).
  useEffect(() => {
    return subscribeLogoutBroadcast(() => setUser(null));
  }, []);

  // SSO liveness probe on tab focus
  useEffect(() => {
    if (!user) return;
    let lastCheck = 0;
    const checkSsoAlive = async () => {
      if (isLoggingOut()) return;
      const now = Date.now();
      if (now - lastCheck < 5000) return;
      lastCheck = now;
      try {
        const res = await authFetch("/api/sso/status");
        if (!res.ok) return;
        const data = await res.json();
        if (!data.authenticated) softLogout();
      } catch {
        // ignore transient errors
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") checkSsoAlive();
    };
    window.addEventListener("focus", checkSsoAlive);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", checkSsoAlive);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [user, softLogout]);

  // Periodic refresh — rotates cookies AND acts as an active invalidation
  // probe so cross-app/admin-initiated revocation kicks the user out fast,
  // even in a background tab that never calls another API.
  //
  // Only a DEFINITIVE auth failure (non-transient !ok from /api/token/refresh,
  // i.e. 401/403) triggers softLogout. Transient failures (network error,
  // 429, 5xx) are ignored — they were the source of the spurious Login-page
  // flashes. authFetch self-heals on 401 for regular traffic, so the worst
  // case of an ignored transient blip is "next real request rotates it."
  useEffect(() => {
    if (!user || isLoggingOut()) return;
    const id = setInterval(async () => {
      const { ok, transient } = await refreshSession();
      if (!ok && !transient) softLogout();
    }, 10 * 60 * 1000);
    return () => clearInterval(id);
  }, [user, softLogout]);

  return (
    <AuthContext.Provider
      value={{ user, loading, login, logout, configError, authFetch }}
    >
      <UserProviderInner user={user}>{children}</UserProviderInner>
    </AuthContext.Provider>
  );
}

function UserProviderInner({ user, children }) {
  const value = useMemo(() => {
    if (!user) {
      return { userId: undefined, permissions: [], hasPermission: () => false, hasAccess: false, canReview: false };
    }
    const permissions = Array.isArray(user.permissions) ? user.permissions : [];
    const hasPermission = (key) => permissions.includes(key);
    return {
      userId: user.id,
      permissions,
      hasPermission,
      hasAccess: permissions.length > 0,
      canReview: hasPermission("admin"),
    };
  }, [user]);
  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}

export function useUser() {
  return useContext(UserContext);
}
